import { useEffect, useId, useMemo, useRef, useState } from "react";
import UiIcon from "../UiIcon";
import { contributionCopy } from "./contributionCopy";
import { formatDateTime } from "./ContributionTimeline";
import "../../styles/cvi-contributions.css";

export const DESCRIPTION_MIN = 10;
export const DESCRIPTION_MAX = 500;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_TYPES = [
    "application/pdf", "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg", "image/png",
];
const CHALLENGE_TYPES = ["EXTERNAL_DEPENDENCY", "RESOURCE_LIMITATION", "PROCESS_ISSUE", "SYSTEM_TOOL", "MANAGEMENT_DECISION", "OTHER"];
const EMPTY = { groupId: "", sub: "", title: "", desc: "", blockerCategory: "", blockerText: "", supportNeed: "", suggestedAction: "", blockerStatus: "none", doc: "", docType: "", docSize: 0, _file: null };

const fromActivity = a => ({
    ...EMPTY,
    groupId: a?.groupId || "", sub: a?.sub || "", title: a?.title || "", desc: a?.desc || "",
    blockerCategory: a?.blockerCategory || "", blockerText: a?.blockerText || "", supportNeed: a?.supportNeed || "",
    suggestedAction: a?.suggestedAction || "", blockerStatus: a?.blockerStatus || "none",
});

// Field rules are the existing ones: a draft needs a category and a title (server rule); a
// submission also needs a subcategory when the category has any, and a 10-character description.
export function validateContribution(form, { activityGroups = [], forSubmit = true } = {}) {
    const group = activityGroups.find(g => g.id === form.groupId);
    const errors = {};
    if (!form.groupId) errors.groupId = "category";
    if (!String(form.title || "").trim()) errors.title = "title";
    if (forSubmit) {
        if (form.groupId && (group?.subs || []).length > 0 && !form.sub) errors.sub = "subcategory";
        if (String(form.desc || "").trim().length < DESCRIPTION_MIN) errors.desc = "description";
    }
    return errors;
}

export function checkFile(file) {
    if (!file) return null;
    if (!ALLOWED_TYPES.includes(file.type)) return "fileType";
    if (file.size > MAX_FILE_BYTES) return "fileSize";
    return null;
}

const formatSize = (bytes, language) => {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toLocaleString(language === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 1 })} ${language === "fr" ? "Mo" : "MB"}`;
    return `${Math.max(1, Math.round(n / 1024))} ${language === "fr" ? "Ko" : "KB"}`;
};

function Field({ id, label, required, optional, error, hint, counter, children, c }) {
    return <div className={`cvi-contrib-field${error ? " has-error" : ""}`}>
        <div className="cvi-contrib-label-row">
            <label htmlFor={id}>{label}{required && <span className="cvi-contrib-req" aria-hidden="true"> *</span>}{optional && <span className="cvi-contrib-opt"> · {c.common.optional}</span>}</label>
            {counter}
        </div>
        {children}
        {error ? <p className="cvi-contrib-error" id={`${id}-error`} role="alert"><UiIcon name="warning" size={13} />{error}</p>
            : hint ? <p className="cvi-contrib-hint" id={`${id}-hint`}>{hint}</p> : null}
    </div>;
}

export default function ContributionForm({
    mode = "create", activity = null, activityGroups = [], language = "en", periodLabel = null, reviewerName = null,
    feedback = null, existingAttachments = null, draftKey = null, onCancel, onSaveDraft, onSubmit, onError, orgLabel = text => text,
}) {
    const c = contributionCopy(language);
    const f = c.form;
    const uid = useId();
    const id = name => `${uid}-${name}`;
    const useLocalDraft = mode === "create" && Boolean(draftKey);

    const [form, setForm] = useState(() => {
        if (mode !== "create") return fromActivity(activity);
        try {
            const saved = useLocalDraft ? localStorage.getItem(draftKey) : null;
            return saved ? { ...EMPTY, ...JSON.parse(saved), _file: null, doc: "", docType: "", docSize: 0 } : EMPTY;
        } catch { return EMPTY; }
    });
    const [touched, setTouched] = useState({});
    const [attempted, setAttempted] = useState(false);
    const [busy, setBusy] = useState(null);
    const [serverError, setServerError] = useState("");
    const [fileError, setFileError] = useState("");
    const [dragging, setDragging] = useState(false);
    const [localSavedAt, setLocalSavedAt] = useState(null);
    const [challengesOpen, setChallengesOpen] = useState(() => Boolean(activity?.blockerCategory || activity?.blockerText || (activity?.blockerStatus && activity.blockerStatus !== "none")));
    const fileInput = useRef(null);
    const summaryRef = useRef(null);

    useEffect(() => {
        if (!useLocalDraft) return undefined;
        const timer = setTimeout(() => {
            try {
                const { _file, doc, docType, docSize, ...rest } = form;
                const empty = Object.entries(rest).every(([key, value]) => value === EMPTY[key]);
                if (empty) { localStorage.removeItem(draftKey); setLocalSavedAt(null); return; }
                localStorage.setItem(draftKey, JSON.stringify(rest));
                setLocalSavedAt(new Date().toISOString());
            } catch { /* storage unavailable: nothing to keep */ }
        }, 500);
        return () => clearTimeout(timer);
    }, [form, draftKey, useLocalDraft]);

    const group = activityGroups.find(g => g.id === form.groupId);
    const subs = group?.subs || [];
    const lockClassification = mode === "revise";
    const errors = useMemo(() => validateContribution(form, { activityGroups, forSubmit: true }), [form, activityGroups]);
    const message = key => ({ groupId: f.errors.category, sub: f.errors.subcategory, title: f.errors.title, desc: f.errors.description(DESCRIPTION_MIN) })[key];
    const shown = key => (attempted || touched[key]) && errors[key] ? message(key) : null;
    const set = (key, value) => { setServerError(""); setForm(prev => ({ ...prev, [key]: value })); };
    const touch = key => setTouched(prev => ({ ...prev, [key]: true }));

    const pickFile = file => {
        setFileError("");
        if (!file) return;
        const problem = checkFile(file);
        if (problem) { setFileError(f.errors[problem]); return; }
        setForm(prev => ({ ...prev, doc: file.name, docType: file.type, docSize: file.size, _file: file }));
    };
    const clearFile = () => { setForm(prev => ({ ...prev, doc: "", docType: "", docSize: 0, _file: null })); if (fileInput.current) fileInput.current.value = ""; };

    const clearLocal = () => { try { if (useLocalDraft) localStorage.removeItem(draftKey); } catch { /* ignore */ } };
    const run = async (kind, handler) => {
        if (busy) return;
        const forSubmit = kind === "submit";
        const problems = validateContribution(form, { activityGroups, forSubmit });
        if (Object.keys(problems).length) {
            setAttempted(true);
            setTouched(prev => ({ ...prev, ...Object.fromEntries(Object.keys(problems).map(k => [k, true])) }));
            requestAnimationFrame?.(() => summaryRef.current?.focus());
            return;
        }
        setBusy(kind);
        setServerError("");
        try {
            await handler(form);
            clearLocal();
        } catch (error) {
            if (error?.savedAsDraft) clearLocal();
            const saved = error?.savedAsDraft || (error?.savedChanges && mode === "draft") ? f.errors.savedAsDraft
                : error?.savedChanges ? f.errors.savedNotResubmitted : null;
            setServerError([saved, error?.message].filter(Boolean).join(" ") || f.errors.generic);
            onError?.(error);
        } finally {
            setBusy(null);
        }
    };

    const visibleErrors = Object.keys(errors).filter(key => attempted && errors[key]);
    const submitLabel = mode === "revise" ? (busy === "submit" ? f.actions.resubmitting : f.actions.resubmit) : (busy === "submit" ? f.actions.submitting : f.actions.submit);
    const describedBy = (key, hint) => [shown(key) ? `${id(key)}-error` : null, hint ? `${id(key)}-hint` : null].filter(Boolean).join(" ") || undefined;
    const challengeTypeLabel = f.challenge.types;

    return <div className="adm-page cvi-contrib" data-cvi-i18n="managed" lang={language}>
        <nav className="cvi-contrib-crumbs"><button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={onCancel}><UiIcon name="chevronLeft" size={14} />{c.common.back}</button></nav>
        <header className="adm-page-header">
            <div className="adm-page-header-copy">
                <h1 className="adm-title">{f.title[mode]}</h1>
                <p className="adm-desc">{f.subtitle[mode]}</p>
            </div>
            {periodLabel && <div className="cvi-contrib-period"><span>{f.period}</span><strong>{periodLabel}</strong></div>}
        </header>

        <form className="cvi-contrib-layout" noValidate onSubmit={event => { event.preventDefault(); run("submit", onSubmit); }}>
            <div className="cvi-contrib-main">
                {visibleErrors.length > 0 && <div className="adm-callout is-danger" role="alert" tabIndex={-1} ref={summaryRef}>
                    <UiIcon name="warning" size={15} /><div><strong>{f.errors.summary(visibleErrors.length)}</strong>
                        <ul className="cvi-contrib-error-list">{visibleErrors.map(key => <li key={key}><a href={`#${id(key)}`}>{message(key)}</a></li>)}</ul></div>
                </div>}

                {mode === "revise" && feedback?.note && <section className="cvi-contrib-feedback is-revision" aria-label={f.feedbackTitle}>
                    <header><UiIcon name="warning" size={15} /><strong>{f.feedbackTitle}</strong>
                        <span>{[feedback.actorName, formatDateTime(feedback.at, language)].filter(Boolean).join(" · ")}</span></header>
                    <p>{feedback.note}</p>
                </section>}

                <section className="cvi-contrib-card">
                    <header className="cvi-contrib-card-head"><div><h2>{f.sections.classification}</h2><p>{f.sections.classificationHelp}</p></div></header>
                    <div className="cvi-contrib-grid">
                        <Field id={id("groupId")} label={f.category} required error={shown("groupId")} c={c}
                            hint={lockClassification ? f.lockedClassification : null}>
                            <select id={id("groupId")} value={form.groupId} disabled={lockClassification} aria-invalid={Boolean(shown("groupId"))}
                                aria-describedby={describedBy("groupId", lockClassification)} onBlur={() => touch("groupId")}
                                onChange={e => setForm(prev => ({ ...prev, groupId: e.target.value, sub: "" }))}>
                                <option value="">{f.categoryPlaceholder}</option>
                                {activityGroups.map(g => <option key={g.id} value={g.id}>{orgLabel(g.label)}</option>)}
                            </select>
                        </Field>
                        <Field id={id("sub")} label={f.subcategory} required={subs.length > 0} error={shown("sub")} c={c}>
                            <select id={id("sub")} value={form.sub} disabled={lockClassification || !group || subs.length === 0}
                                aria-invalid={Boolean(shown("sub"))} aria-describedby={describedBy("sub")} onBlur={() => touch("sub")}
                                onChange={e => set("sub", e.target.value)}>
                                <option value="">{group ? f.subcategoryPlaceholder : f.subcategoryFirst}</option>
                                {subs.map(s => <option key={s} value={s}>{orgLabel(s)}</option>)}
                            </select>
                        </Field>
                    </div>
                </section>

                <section className="cvi-contrib-card">
                    <header className="cvi-contrib-card-head"><div><h2>{f.sections.details}</h2><p>{f.sections.detailsHelp}</p></div></header>
                    <Field id={id("title")} label={f.titleLabel} required error={shown("title")} c={c}>
                        <input id={id("title")} type="text" value={form.title} maxLength={200} placeholder={f.titlePlaceholder}
                            aria-invalid={Boolean(shown("title"))} aria-describedby={describedBy("title")}
                            onBlur={() => touch("title")} onChange={e => set("title", e.target.value)} />
                    </Field>
                    <Field id={id("desc")} label={f.description} required error={shown("desc")} hint={f.impactNote} c={c}
                        counter={<span className={`cvi-contrib-counter${form.desc.length >= DESCRIPTION_MAX ? " is-max" : ""}`} aria-live="polite">{f.counter(form.desc.length, DESCRIPTION_MAX)}</span>}>
                        <textarea id={id("desc")} rows={6} value={form.desc} placeholder={f.descriptionPlaceholder}
                            aria-invalid={Boolean(shown("desc"))} aria-describedby={describedBy("desc", true)}
                            onBlur={() => touch("desc")} onChange={e => set("desc", e.target.value.slice(0, DESCRIPTION_MAX))} />
                    </Field>
                </section>

                <section className="cvi-contrib-card">
                    <header className="cvi-contrib-card-head"><div><h2>{f.sections.evidence} <span className="cvi-contrib-opt">· {c.common.optional}</span></h2><p>{f.sections.evidenceHelp}</p></div></header>
                    {existingAttachments && <div className="cvi-contrib-existing"><h3>{f.existingFiles}</h3>{existingAttachments}</div>}
                    {form._file ? <div className="cvi-contrib-file">
                        <span className="cvi-contrib-file-icon" aria-hidden="true"><UiIcon name="paperclip" size={16} /></span>
                        <div><strong>{form.doc}</strong><small>{formatSize(form.docSize, language)} · {f.upload.selected}</small></div>
                        <button type="button" className="adm-btn adm-btn--sm" onClick={() => fileInput.current?.click()}>{f.upload.replace}</button>
                        <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={clearFile}>{f.upload.remove}</button>
                    </div> : <div className={`cvi-contrib-drop${dragging ? " is-dragging" : ""}${fileError ? " has-error" : ""}`}
                        onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
                        onDrop={e => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer?.files?.[0]); }}>
                        <span className="cvi-contrib-drop-icon" aria-hidden="true"><UiIcon name="paperclip" size={18} /></span>
                        <div><button type="button" className="cvi-contrib-link" onClick={() => fileInput.current?.click()}>{f.upload.cta}</button> <span>{f.upload.or}</span>
                            <small>{f.upload.formats}</small></div>
                    </div>}
                    <input ref={fileInput} id={id("file")} type="file" className="cvi-contrib-sr" tabIndex={-1} aria-hidden="true"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => pickFile(e.target.files?.[0])} />
                    {fileError && <p className="cvi-contrib-error" role="alert"><UiIcon name="warning" size={13} />{fileError}</p>}
                </section>

                <section className="cvi-contrib-card cvi-contrib-challenges">
                    <button type="button" className="cvi-contrib-disclosure" aria-expanded={challengesOpen} onClick={() => setChallengesOpen(v => !v)}>
                        <span><strong>{f.sections.challenges}</strong> <span className="cvi-contrib-opt">· {c.common.optional}</span><small>{f.sections.challengesHelp}</small></span>
                        <UiIcon name="chevronDown" size={16} />
                    </button>
                    {challengesOpen && <div className="cvi-contrib-grid">
                        <Field id={id("blockerCategory")} label={f.challenge.type} c={c}>
                            <select id={id("blockerCategory")} value={form.blockerCategory}
                                onChange={e => { const value = e.target.value; setForm(prev => ({ ...prev, blockerCategory: value, blockerStatus: value && prev.blockerStatus === "none" ? "open" : !value ? "none" : prev.blockerStatus })); }}>
                                <option value="">{f.challenge.none}</option>
                                {CHALLENGE_TYPES.map(key => <option key={key} value={key}>{challengeTypeLabel[key]}</option>)}
                            </select>
                        </Field>
                        <Field id={id("blockerStatus")} label={f.challenge.status} c={c}>
                            <select id={id("blockerStatus")} value={form.blockerStatus} onChange={e => set("blockerStatus", e.target.value)}>
                                {Object.entries(f.challenge.statuses).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </Field>
                        <div className="is-wide"><Field id={id("blockerText")} label={f.challenge.description} c={c}>
                            <textarea id={id("blockerText")} rows={3} value={form.blockerText} onChange={e => set("blockerText", e.target.value)} />
                        </Field></div>
                        <Field id={id("supportNeed")} label={f.challenge.support} c={c}>
                            <textarea id={id("supportNeed")} rows={3} value={form.supportNeed} onChange={e => set("supportNeed", e.target.value)} />
                        </Field>
                        <Field id={id("suggestedAction")} label={f.challenge.action} c={c}>
                            <textarea id={id("suggestedAction")} rows={3} value={form.suggestedAction} onChange={e => set("suggestedAction", e.target.value)} />
                        </Field>
                    </div>}
                </section>
            </div>

            <aside className="cvi-contrib-aside">
                <div className="cvi-contrib-card cvi-contrib-actions">
                    {serverError && <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{serverError}</div></div>}
                    <div className="cvi-contrib-buttons">
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={Boolean(busy)} aria-busy={busy === "submit"}>
                            {busy === "submit" ? <span className="cvi-contrib-spinner" aria-hidden="true" /> : <UiIcon name="send" size={15} />}{submitLabel}
                        </button>
                        {mode !== "revise" && onSaveDraft && <button type="button" className="adm-btn" disabled={Boolean(busy)} aria-busy={busy === "draft"} onClick={() => run("draft", onSaveDraft)}>
                            {busy === "draft" ? <span className="cvi-contrib-spinner is-dark" aria-hidden="true" /> : <UiIcon name="archive" size={15} />}{busy === "draft" ? f.actions.savingDraft : f.actions.saveDraft}
                        </button>}
                        <button type="button" className="adm-btn adm-btn--ghost" disabled={Boolean(busy)} onClick={onCancel}>{c.common.cancel}</button>
                    </div>
                    <p className="cvi-contrib-hint">{f.actions.submitHint(reviewerName)}{mode !== "revise" && onSaveDraft ? ` ${f.actions.draftHint}` : ""}</p>
                    {useLocalDraft && <p className="cvi-contrib-local">
                        <span>{localSavedAt ? f.localDraft.saved(formatDateTime(localSavedAt, language)) : f.localDraft.idle}</span>
                        {localSavedAt && <button type="button" className="cvi-contrib-link" onClick={() => { clearLocal(); setForm(EMPTY); setTouched({}); setAttempted(false); setLocalSavedAt(null); }}>{f.localDraft.clear}</button>}
                    </p>}
                </div>
            </aside>
        </form>
    </div>;
}
